<template>
  <div>
    <b-container class="py-3 px-0">
      <div class="ota-picker">
        <b-col cols="12" md="5 px-0" lg="4 pl-0">
          <date-range-picker-mobile
            v-if="isMobile"
            ref="dateRangePicker"
            :value="dateRange"
            :config="datePickerConfig"
            :mode="datePickerMode"
            plain
            @input="handleDateRangeChange"
          />
          <div v-else>
            <input-container-v2
              :label="$t('searchBar.checkInAndOut')"
              icon-name="icon-calendar"
              data-cy="checkin-checkout"
            >
              <date-range-picker
                :value="dateRange"
                :config="datePickerConfig"
                :mode="datePickerMode"
                hidden-mode
                show-date-range-label
                @input="handleDateRangeChange"
                @onClose="handleDatePickerClose"
              />
            </input-container-v2>
          </div>
        </b-col>
        <template v-if="$preview">
          <b-col cols="12" md="5" lg="4" class="px-0 traveller-picker">
            <multiple-rooms-picker-v2
              v-if="searchForm.rooms"
              :rooms="searchForm.rooms"
              :shared-bed-kid-count-per-adult="sharedBedKidCountPerAdult"
              :is-multi-room-search-enabled="isMultiRoomSearchEnabled"
              @addRoom="addRoom"
              @removeRoom="removeRoom"
              @changeRoomAttr="handleRoomAttrChange"
              @changeTierCode="changeTierCode"
              @toggle="closePicker"
            />
          </b-col>
        </template>
        <template v-else>
          <template v-if="isTiersMode">
            <div class="adult-kid-container">
              <b-col cols="8" sm="7" lg="6" class="tier-picker">
                <adult-picker-v2
                  :adults="searchForm.adults"
                  :max-adult-count="initialSetting.max_guests_per_room"
                  :no-padding-icon="true"
                  class="w-100"
                  @change="changeAdults"
                >
                  <kids-tiers-picker-v2
                    v-if="searchForm.kidsTiers"
                    :kids-tiers="searchForm.kidsTiers"
                    :no-padding-icon="true"
                    @changeTierCount="handleTierChange"
                    @reset="resetKidsTiersCriteria"
                    @toggle="closePicker"
                  />
                </adult-picker-v2>
              </b-col>
              <b-col class="px-0 room-picker" cols="4" sm="5" lg="4">
                <rooms-picker-v2
                  v-if="isMultiRoomSearchEnabled && searchForm.room_count"
                  :no-padding-icon="true"
                  :room-count="searchForm.room_count"
                  @change="changeSearchRoomCount"
                />
              </b-col>
            </div>
          </template>
          <template v-else>
            <b-col cols="12" md="5" lg="4" class="px-0 traveller-picker">
              <traveller-picker-v2
                :rooms="searchForm.rooms"
                :shared-bed-kid-count-per-adult="sharedBedKidCountPerAdult"
                :is-multi-room-search-enabled="isMultiRoomSearchEnabled"
                plain
                @addRoom="addRoom"
                @removeRoom="removeRoom"
                @changeRoomAttr="handleRoomAttrChange"
                @toggle="closePicker"
              />
            </b-col>
          </template>
        </template>
      </div>
    </b-container>
  </div>
</template>

<script>
import { mapGetters } from 'vuex'
import { isMobile } from 'utilities/domOperation'
import searchFormMixin from 'mixins/searchFormMixin'
import paths from 'routes/paths'
import { addDay, formatDate } from 'utilities/date'
import EventBus from 'utilities/EventBus'
import DateRangePickerInventoryMixin from 'components/SearchBar/DateRangePickerInventoryMixin'
import DateRangePicker from 'components/SearchBar/DateRangePicker'
import DateRangePickerMobile from 'components/SearchBar/DateRangePickerMobile'
import TravellerPickerV2 from 'components/SearchBar/TravellerPickerV2'
import KidsTiersPickerV2 from 'components/SearchBar/KidsTiersPickerV2'
import MultipleRoomsPickerV2 from 'components/SearchBar/MultipleRoomsPickerV2'
import AdultPickerV2 from 'components/SearchBar/AdultPickerV2'
import RoomsPickerV2 from 'components/SearchBar/RoomsPickerV2'
import InputContainerV2 from 'components/InputContainerV2'

export default {
  name: 'SearchBar',
  components: {
    DateRangePicker,
    DateRangePickerMobile,
    TravellerPickerV2,
    KidsTiersPickerV2,
    AdultPickerV2,
    RoomsPickerV2,
    MultipleRoomsPickerV2,
    InputContainerV2
  },
  mixins: [searchFormMixin, DateRangePickerInventoryMixin],
  props: {
    loading: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      dirty: false,
      dateRange: []
    }
  },
  computed: {
    isMobile,
    ...mapGetters({
      searchForm: 'search/getSearchForm',
      initialSetting: 'setting/getInit',
      isTiersMode: 'setting/isTiersMode',
      kidsSetting: 'setting/getKidsSetting',
      totalAdultsCount: 'search/getTotalAdultsCount',
      isMultiRoomSearchEnabled: 'setting/isMultiRoomSearchEnabled',
      isUndated: 'search/isUndated' // use for DateRangePickerInventoryMixin
    }),
    datePickerMode() {
      return this.searchForm.is_day_use ? 'single' : 'range'
    }
  },
  watch: {
    totalAdultsCount() {
      this.$store.dispatch('search/clearSelectedRooms')
    }
  },
  created() {
    this.dateRange = [this.searchForm.checkin, this.searchForm.checkout]
    EventBus.$on('search', (searchAttr) => {
      if (searchAttr.checkin && searchAttr.checkout) {
        this.dateRange = [searchAttr.checkin, searchAttr.checkout]
      }
      this.search(searchAttr)
    })
  },
  methods: {
    toggleSelectorDropdown(name) {
      this.$refs[name].toggle()
    },
    search(data) {
      if (data) {
        this.$store.dispatch('search/setSearchForm', data)
        this.dirty = true
      }
      if (this.loading || !this.dirty) return
      this.$emit('search')
      this.dirty = false
    },
    handleDateRangeChange(selectedDates) {
      this.dateRange = selectedDates
      this.search({
        checkin: this.dateRange[0],
        checkout: this.dateRange[1] ?? this.dateRange[0]
      })
    },
    handleDatePickerClose({ selectedDates, dateStr }) {
      if (!this.searchForm.is_day_use && selectedDates.length === 1) {
        const checkin = formatDate(selectedDates[0])
        const checkout = addDay(checkin, 1)
        this.dateRange = [checkin, checkout]
        this.search({
          checkin,
          checkout
        })
      }
    },
    changeSearchRoomCount(roomCount) {
      this.$store.dispatch('search/setSearchForm', { room_count: roomCount })
      //   this.$emit('changeUrl', { room_count: roomCount })
    },
    changeAdults(adults) {
      this.search({ adults })
    },
    handleTierChange(...args) {
      this.changeTierCount(...args)
      this.dirty = true
    },
    changeNights(nights) {
      this.search({ nights })
    },
    closePicker() {
      this.search()
    },
    handleRoomAttrChange(...args) {
      this.changeRoomAttr(...args)
      this.dirty = true
    }
  }
}
</script>

<style lang="scss" scoped>
@import '~stylesheets/vars.scss';
@import '~stylesheets/mixins.scss';
@import '~stylesheets/search-widget.scss';

.ota-picker {
  display: flex;
  flex-wrap: wrap;
  align-items: flex-start;

  .adult-kid-container {
    width: calc(100% - 8px);
    display: flex;
    flex-wrap: wrap;

    .tier-picker {
      padding-left: 0;
      padding-right: 8px;
      flex: 0 0 80%;
      max-width: 80%;
    }

    .room-picker {
      flex: 0 0 38%;
      max-width: 38%;

      .room-container {
        width: 100%;
      }
    }
  }

  @media (min-width: 375px) {
    .adult-kid-container {
      width: 100%;
      flex-wrap: nowrap;

      .tier-picker {
        flex: 0 0 66.66667%;
        max-width: 66.66667%;
      }

      .room-picker {
        flex: unset;
        max-width: unset;
      }
    }
  }

  @media (min-width: 576px) {
    .adult-kid-container {
      .tier-picker {
        flex: 0 0 58.33333%;
        max-width: 58.33333%;
      }
    }
  }

  @media (min-width: 768px) {
    .adult-kid-container {
      width: 58.33333%;
      padding-left: 16px;

      .tier-picker {
        flex: 0 0 58.33333%;
        max-width: 58.33333%;
      }

      .room-picker {
        .room-container {
          width: 60%;
        }
      }
    }

    .traveller-picker {
      padding-left: 16px !important;
    }
  }

  @media (min-width: 992px) {
    flex-wrap: unset;

    .adult-kid-container {
      width: 100%;
      padding-left: unset;

      .tier-picker {
        padding-left: 16px;
        padding-right: 16px;
        flex: 0 0 41.66667%;
        max-width: 41.66667%;
      }
    }
  }
}

.search {
  padding: 0 8px;
}

.search-wrapper {
  background: $white;
  box-shadow: 0 5px 4px 0 rgba(0, 0, 0, 0.25);
  padding: 0 0 10px;
}

.search-container {
  width: 100%;
  padding: 12px 0;
  background: rgba(255, 255, 255, 0.9);
  border-bottom: 1px solid $border-line;

  @include media-breakpoint-down(md) {
    padding: 12px 0 0;
  }
}

.search-btn {
  min-width: 110px;
}
:deep {
  .flatpickr-input {
    padding-left: 40px;
  }
  .title-block {
    .icon {
      display: none;
    }
    .title {
      padding: 0;
      font-size: 16px;
      font-weight: 400;
      margin-bottom: 5px;
    }
  }
}
</style>
